import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { Button } from './Button';

export interface ModalProps {
  title: string;
  width?: number;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

// I modali aperti, dal più vecchio al più nuovo: Esc chiude solo quello in cima (un confirm
// sopra il modale del git non deve chiudere anche il secondo).
const open: object[] = [];

export function Modal({ title, width = 640, onClose, children, footer }: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  const previously = useRef<Element | null>(null);
  // onClose cambia a ogni render di chi apre il modale: se fosse una dipendenza dell'effetto,
  // ogni tasto premuto riporterebbe il focus al primo elemento (la X)
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    previously.current = document.activeElement;
    // il focus va al primo campo; se un campo l'ha già preso (autoFocus), resta lì
    if (!panel.current?.contains(document.activeElement)) {
      const field = panel.current?.querySelector<HTMLElement>('input, textarea, select');
      const any = panel.current?.querySelector<HTMLElement>('button, [tabindex]');
      (field ?? any)?.focus();
    }
    const me = {};
    open.push(me);
    // Esc in fase bubble: una select aperta dentro il modale lo usa prima (preventDefault) per
    // chiudere solo il suo menu, e il modale resta aperto con quello che si è scritto
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || open[open.length - 1] !== me) return;
      e.preventDefault();
      close.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !panel.current || open[open.length - 1] !== me) return;
      const nodes = [
        ...panel.current.querySelectorAll<HTMLElement>(
          'button, input, textarea, select, a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((n) => !n.hasAttribute('disabled'));
      if (nodes.length === 0) return;
      const idx = nodes.indexOf(document.activeElement as HTMLElement);
      const next = e.shiftKey ? idx - 1 : idx + 1;
      if (next < 0 || next >= nodes.length) {
        e.preventDefault();
        nodes[e.shiftKey ? nodes.length - 1 : 0].focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('keydown', onEscape);
    return () => {
      open.splice(open.indexOf(me), 1);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('keydown', onEscape);
      (previously.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  // in un portale su <body>: un antenato con `backdrop-filter` o `transform` (l'header) farebbe
  // da contenitore al `position: fixed` e il modale si centrerebbe su di lui, non sulla finestra
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'var(--overlay)',
        backdropFilter: 'blur(2px)',
        display: 'grid',
        // la colonna resta larga quanto lo schermo: senza, il pannello la allarga e su
        // telefono esce dal bordo
        gridTemplateColumns: 'minmax(0, 1fr)',
        placeItems: 'center',
        padding: 16,
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="anim-toast"
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: '100%',
          maxHeight: 'min(84vh, 720px)',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-2xl)',
          boxShadow: 'var(--shadow-3)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: 56,
            flex: 'none',
            padding: '0 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '1px solid var(--border)',
          }}
        >
          <h2 className="t-h2" style={{ flex: 1 }}>
            {title}
          </h2>
          <Button variant="icon" icon="x" aria-label="Chiudi" onClick={onClose} />
        </div>
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              height: 64,
              flex: 'none',
              padding: '0 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              borderTop: '1px solid var(--border)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
